import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, useTheme, useMediaQuery, CircularProgress, Box, Skeleton, Stack } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { LocalizedCurrencyText } from "../../../Shared/components/LocalizedCurrency";
import { useCartButton } from "./useCartButton";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
const useStyles = makeStyles((theme) => ({
    customIconWrapper: (props) => ({
        backgroundColor: props.isIconWhite ? theme.palette.primary.main : undefined,
        padding: theme.spacing(0.5),
        lineHeight: 1,
        borderRadius: theme.shape.borderRadius,
    }),
    mobileCartButton: {
        width: "25%",
        backgroundColor: `${theme.palette.common.white}`,
        border: `solid 1px ${theme.palette.primary.main}`,
    },
    desktopCartButton: {
        borderRadius: "5px",
        padding: "10px",
        position: "fixed",
        width: "120px",
        height: "120px",
        top: "240px",
        right: 0,
        backgroundColor: `${theme.palette.common.white}`,
        zIndex: 1100,
        opacity: 1,
        transition: "opacity 0.4s",
        border: `solid 1px ${theme.palette.primary.main}`,
    },
}));
/**
 * Renders cart button for mobile and desktop with icon and price
 * Renders in a portal to isolate from main component tree for better performance
 * @param isUpdaingPrice
 * @param pricingBreakdown
 * @param balance
 * @param onClick
 * @returns
 */
export const CartButton = ({ isUpdatingPrice, 
// TODO REPLACE AND REMOVE ALL INSTANCES OF AND REFERENCES TO pricingBreakdown WITH EQUIVALENT FROM BALANCE OBJECT
pricingBreakdown, balance, onClick, }) => {
    var _a, _b;
    const { customCartIconUrl, usesCustomCartIcon, isCustomCartIconWhite, usesSimplifiedPricingBreakdown } = useCartButton();
    const theme = useTheme();
    const styleProps = {
        isIconWhite: isCustomCartIconWhite,
    };
    const classes = useStyles(styleProps);
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
    const [loading, setLoading] = useState(true);
    const [portalContainer, setPortalContainer] = useState(null);
    // Create or get the portal container
    useEffect(() => {
        let container = document.getElementById("cart-button-portal");
        if (!container) {
            container = document.createElement("div");
            container.id = "cart-button-portal";
            container.style.position = "fixed";
            container.style.top = "0";
            container.style.left = "0";
            container.style.pointerEvents = "none"; // Allow clicks to pass through container
            container.style.zIndex = "1100";
            document.body.appendChild(container);
        }
        setPortalContainer(container);
        // Cleanup function
        return () => {
            // Don't remove the container on cleanup as other instances might be using it
        };
    }, []);
    useEffect(() => {
        const image = new Image();
        image.src = customCartIconUrl;
        image.onload = () => {
            setLoading(false);
        };
        // Handle image loading error if needed
        image.onerror = () => {
            setLoading(false);
        };
    }, [customCartIconUrl]);
    const cartIcon = usesCustomCartIcon && customCartIconUrl ? (_jsx(Box, Object.assign({ className: classes.customIconWrapper }, { children: loading ? (_jsx(Skeleton, { variant: "rectangular", width: "100%", height: isMobile ? "22px" : "30px" }) // Adjust width and height as needed
        ) : (_jsx("img", { style: { width: "auto", height: isMobile ? "22px" : "30px" }, src: customCartIconUrl })) }))) : (_jsx(ShoppingCartIcon, { color: "primary", fontSize: isMobile ? "small" : "medium" }));
    // TODO REPLACE AND REMOVE ALL INSTANCES OF AND REFERENCES TO pricingBreakdown WITH EQUIVALENT FROM BALANCE OBJECT
    const showNormalPricingBreakdown = !usesSimplifiedPricingBreakdown && pricingBreakdown && pricingBreakdown.total !== 0;
    // TODO REPLACE AND REMOVE ALL INSTANCES OF AND REFERENCES TO pricingBreakdown WITH EQUIVALENT FROM BALANCE OBJECT
    const showSimplifiedPricingBreakdown = usesSimplifiedPricingBreakdown && pricingBreakdown && pricingBreakdown.simpleTotal !== 0;
    // TODO REPLACE AND REMOVE ALL INSTANCES OF AND REFERENCES TO pricingBreakdown WITH EQUIVALENT FROM BALANCE OBJECT
    const simplePrice = showSimplifiedPricingBreakdown && pricingBreakdown.simpleTotal - ((_a = pricingBreakdown.totalDayBankValue) !== null && _a !== void 0 ? _a : 0);
    // TODO REPLACE AND REMOVE ALL INSTANCES OF AND REFERENCES TO pricingBreakdown WITH EQUIVALENT FROM BALANCE OBJECT
    const normalPrice = showNormalPricingBreakdown && pricingBreakdown.total - ((_b = pricingBreakdown.totalDayBankValue) !== null && _b !== void 0 ? _b : 0);
    const total = normalPrice || simplePrice;
    const cartButtonContent = (_jsx(Button, Object.assign({ className: isMobile ? classes.mobileCartButton : classes.desktopCartButton, onClick: onClick, variant: "outlined", sx: {
            minHeight: isMobile ? "inherit" : "auto",
            pointerEvents: "auto", // Enable clicks on the button itself
        } }, { children: _jsxs(Stack, Object.assign({ direction: "column", alignItems: "center", sx: { gap: theme.spacing(isMobile ? 0 : 1) } }, { children: [cartIcon, isUpdatingPrice ? (_jsx(CircularProgress, { color: "primary", size: isMobile ? "15px" : "22px" })) : ((showNormalPricingBreakdown || showSimplifiedPricingBreakdown) && (_jsx(LocalizedCurrencyText, { value: total > 0 ? total : 0 })))] })) })));
    // Don't render anything on mobile or if portal container isn't ready
    if (isMobile || !portalContainer) {
        return isMobile ? cartButtonContent : null;
    }
    // Render desktop version in portal
    return createPortal(cartButtonContent, portalContainer);
};
